import { useLayoutEffect, useMemo, useState } from 'react'
import { useLocalStorage, useWindowSize } from 'react-use'
import clamp from 'lodash/clamp'
import isFinite from 'lodash/isFinite'
import type { RefObject } from 'react'
import type { BubbleBox, BubbleBounds } from './computeBubbleBounds'
import { computeBubbleBounds } from './computeBubbleBounds'

export const AI_CHAT_BUBBLE_OFFSET_STORAGE_KEY = 'ai-chat-bubble:offset'

export type BubbleOffset = { x: number; y: number }
export type UseChatBubbleOffsetProps = { bubbleRef?: RefObject<HTMLElement> }
export type UseChatBubbleOffsetResult = {
  offset: BubbleOffset
  bounds?: BubbleBounds
  saveOffset: (offset: BubbleOffset) => void
  resetOffset: () => void
}

const DEFAULT_BUBBLE_OFFSET: BubbleOffset = { x: 0, y: 0 }

/**
 * Owns the saved chat-bubble drag translation (`ai-chat-bubble:offset`) and
 * the viewport bounds that keep the whole bubble on screen.
 *
 * The key is read without an initial value so a first visit never writes;
 * only an ended drag writes, and Reset position removes the key. The saved
 * spot is clamped into the viewport on read only — a smaller viewport renders
 * the bubble inside without touching storage, so growing back restores it.
 */
export const useChatBubbleOffset = ({ bubbleRef }: UseChatBubbleOffsetProps = {}): UseChatBubbleOffsetResult => {
  const [savedOffset, saveOffset, resetOffset] = useLocalStorage<BubbleOffset>(AI_CHAT_BUBBLE_OFFSET_STORAGE_KEY)
  const { width: viewportWidth, height: viewportHeight } = useWindowSize()
  const [box, setBox] = useState<BubbleBox>()

  // Measure the bubble's untransformed layout box — the drag transform must
  // not feed the bounds, and react-draggable reads these same layout values
  // when it clamps a live drag.
  useLayoutEffect(() => {
    const node = bubbleRef?.current
    if (!node) return
    setBox({ left: node.offsetLeft, top: node.offsetTop, width: node.offsetWidth, height: node.offsetHeight })
  }, [bubbleRef, viewportWidth, viewportHeight])

  const bounds = useMemo(
    () => (box ? computeBubbleBounds({ box, viewportWidth, viewportHeight }) : undefined),
    [box, viewportWidth, viewportHeight]
  )

  const offset = useMemo<BubbleOffset>(() => {
    const saved =
      savedOffset && isFinite(savedOffset.x) && isFinite(savedOffset.y) ? savedOffset : DEFAULT_BUBBLE_OFFSET
    if (!bounds) return saved
    return { x: clamp(saved.x, bounds.left, bounds.right), y: clamp(saved.y, bounds.top, bounds.bottom) }
  }, [savedOffset, bounds])

  return { offset, bounds, saveOffset, resetOffset }
}
